import { useState } from 'preact/hooks';
import { Shield, Lock, Sparkles } from 'lucide-preact';

interface ParentGateProps {
  onVerified: () => void;
}

export function ParentGate({ onVerified }: ParentGateProps) {
  const [step, setStep] = useState<'intro' | 'math' | 'consent'>('intro');
  const [mathAnswer, setMathAnswer] = useState('');
  const [error, setError] = useState('');

  // Generate a simple math problem that kids can't easily solve
  const [mathProblem] = useState(() => {
    const a = Math.floor(Math.random() * 20) + 15;
    const b = Math.floor(Math.random() * 20) + 10;
    return { a, b, answer: a * b };
  });

  const handleMathSubmit = (e: Event) => {
    e.preventDefault();
    if (parseInt(mathAnswer, 10) === mathProblem.answer) {
      setStep('consent');
      setError('');
    } else {
      setError('Incorrect answer. Please try again.');
      setMathAnswer('');
    }
  };

  if (step === 'intro') {
    return (
      <div className="min-h-screen gradient-warm flex items-center justify-center p-4">
        <div className="bg-cream rounded-3xl border-ornate max-w-md w-full p-8 animate-in">
          <div className="text-center">
            <div className="w-16 h-16 rounded-2xl bg-parchment border-ornate flex items-center justify-center mx-auto mb-6">
              <Shield className="w-8 h-8 text-rust" strokeWidth={1.5} />
            </div>
            <h1 className="text-2xl font-display font-medium text-ink mb-4">Parent Required</h1>
            <p className="text-ink/70 mb-8 leading-relaxed">
              Storyseed is designed for children. Before your child can use this app, a parent or
              guardian must verify their presence and review our privacy practices.
            </p>
            <button
              onClick={() => setStep('math')}
              className="w-full px-6 py-4 bg-rust text-cream rounded-2xl font-medium
                       shadow-story hover:shadow-story-hover transition-all cursor-pointer"
            >
              I am a Parent or Guardian
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (step === 'math') {
    return (
      <div className="min-h-screen gradient-warm flex items-center justify-center p-4">
        <div className="bg-cream rounded-3xl border-ornate max-w-md w-full p-8 animate-in">
          <div className="w-16 h-16 rounded-2xl bg-parchment border-ornate flex items-center justify-center mx-auto mb-6">
            <Lock className="w-8 h-8 text-rust" strokeWidth={1.5} />
          </div>
          <h1 className="text-2xl font-display font-medium text-ink mb-4 text-center">
            Parent Verification
          </h1>
          <p className="text-ink/70 mb-6 text-center">
            Please solve this math problem to verify you are an adult:
          </p>
          <form onSubmit={handleMathSubmit}>
            <div className="mb-6">
              <label className="block text-2xl font-display font-medium text-ink text-center mb-4">
                {mathProblem.a} × {mathProblem.b} = ?
              </label>
              <input
                type="number"
                value={mathAnswer}
                onInput={(e) => setMathAnswer((e.target as HTMLInputElement).value)}
                className="w-full px-4 py-3 text-lg text-center bg-parchment border border-gold/30 rounded-xl
                         text-ink focus:outline-none focus:ring-2 focus:ring-gold/50 focus:border-gold/50"
                placeholder="Enter your answer"
                autoFocus
              />
              {error && <p className="mt-2 text-sm text-berry text-center">{error}</p>}
            </div>
            <button
              type="submit"
              className="w-full px-6 py-4 bg-rust text-cream rounded-2xl font-medium
                       shadow-story hover:shadow-story-hover transition-all cursor-pointer"
            >
              Verify
            </button>
          </form>
          <button
            type="button"
            onClick={() => setStep('intro')}
            className="w-full mt-4 text-sm text-ink/50 hover:text-ink transition-colors cursor-pointer"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen gradient-warm flex items-center justify-center p-4">
      <div className="bg-cream rounded-3xl border-ornate max-w-lg w-full p-8 animate-in">
        <div className="w-16 h-16 rounded-2xl bg-parchment border-ornate flex items-center justify-center mx-auto mb-6">
          <Sparkles className="w-8 h-8 text-rust" strokeWidth={1.5} />
        </div>
        <h1 className="text-2xl font-display font-medium text-ink mb-6 text-center">
          Parental Consent
        </h1>

        <div className="text-ink/70 mb-6 space-y-4">
          <h3 className="text-lg font-display font-medium text-ink">Privacy Summary</h3>
          <ul className="list-disc pl-5 space-y-2 text-sm">
            <li>
              <strong className="text-ink">No accounts required:</strong> Children use the app without creating an
              account.
            </li>
            <li>
              <strong className="text-ink">Local storage only:</strong> Stories are saved on this device only, not on our
              servers.
            </li>
            <li>
              <strong className="text-ink">No personal data collection:</strong> We do not collect, store, or share any
              personal information about your child.
            </li>
            <li>
              <strong className="text-ink">Hero's name in stories:</strong> The name entered is used only to
              personalize the story text and is never transmitted to our servers in a way that
              identifies your child.
            </li>
            <li>
              <strong className="text-ink">AI-generated content:</strong> Stories are generated using AI. Content is
              filtered for age-appropriateness.
            </li>
          </ul>

          <h3 className="text-lg font-display font-medium text-ink mt-6">Your Rights</h3>
          <ul className="list-disc pl-5 space-y-2 text-sm">
            <li>You can delete all stored stories at any time from the Library.</li>
            <li>You can revoke this consent at any time from Settings.</li>
            <li>
              Contact us at{' '}
              <a href="mailto:privacy@straitstreet.co" className="text-rust hover:underline">
                privacy@straitstreet.co
              </a>{' '}
              with any questions.
            </li>
          </ul>
        </div>

        <div className="border-t border-gold/20 pt-6">
          <button
            onClick={onVerified}
            className="w-full px-6 py-4 bg-rust text-cream rounded-2xl font-medium
                     shadow-story hover:shadow-story-hover transition-all cursor-pointer"
          >
            I Consent - Let My Child Use Storyseed
          </button>
          <p className="mt-4 text-xs text-ink/50 text-center">
            By clicking above, you confirm you are this child's parent or legal guardian and consent
            to the practices described.
          </p>
        </div>
      </div>
    </div>
  );
}
